import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useProjectSiteNavigation, useProjectSiteParams } from '@/shared/lib';
import { RouteNames } from '@/shared/config/routes';
import {
  useGetServiceRequestDetailQuery,
  useLinkServiceRequestToConsignmentMutation
} from '@/entities/service-request';
import { transformSrfDetailToForm } from '@/widgets/consignment-edit-form';

export function useConsignmentCreate() {
  const { t } = useTranslation();
  const { navigateWithContext } = useProjectSiteNavigation();
  const { projectId, siteId } = useProjectSiteParams();
  const [searchParams] = useSearchParams();
  const [linkConsignment] = useLinkServiceRequestToConsignmentMutation();

  const serviceRequestParam = searchParams.get('serviceRequestId');
  const serviceRequestId = serviceRequestParam ? Number(serviceRequestParam) : undefined;
  const hasSrf = serviceRequestId != null && !Number.isNaN(serviceRequestId) && projectId != null && siteId != null;

  const { data: srfDetail, isFetching: isSrfLoading } = useGetServiceRequestDetailQuery(
    { id: serviceRequestId ?? 0, projectId: projectId ?? 0, siteId: siteId ?? 0 },
    { skip: !hasSrf }
  );

  const prefillValues = useMemo(() => (srfDetail ? transformSrfDetailToForm(srfDetail) : undefined), [srfDetail]);

  const handleSave = useCallback(
    async (result: unknown) => {
      const id = (result as { id?: string } | null | undefined)?.id;

      if (id != null && hasSrf && serviceRequestId != null && projectId != null && siteId != null) {
        try {
          await linkConsignment({ id: serviceRequestId, consignmentId: id, projectId, siteId }).unwrap();
        } catch (error) {
          console.error('Failed to link service request to consignment:', error);
          toast.error(
            t('serviceRequests.linkConsignmentError', 'Consignment created, but copying service request lines failed')
          );
        }
      }

      if (id != null) {
        navigateWithContext(RouteNames.CONSIGNMENT_EDIT, { consignmentId: id });
      } else {
        navigateWithContext(RouteNames.CONSIGNMENTS);
      }
    },
    [navigateWithContext, linkConsignment, hasSrf, serviceRequestId, projectId, siteId, t]
  );

  const handleBack = useCallback(() => {
    navigateWithContext(RouteNames.CONSIGNMENTS);
  }, [navigateWithContext]);

  return {
    handleSave,
    handleBack,
    serviceRequestId: hasSrf ? serviceRequestId : undefined,
    srfDetail,
    prefillValues,
    isSrfLoading: hasSrf ? isSrfLoading : false
  };
}
